/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Grid, Tooltip, IconButton, Box } from "@mui/material";
import { Description as DescriptionIcon } from "@mui/icons-material";
import { formatResolvedDate } from "@/common/utils/utils";

// Shared state for all NotesCell instances to coordinate tooltip behavior
const tooltipState = {
  openTooltips: {} as Record<string, boolean>,
  listeners: new Set<(state: Record<string, boolean>) => void>(),
  setOpenTooltips: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => {
    const newState = updater(tooltipState.openTooltips);
    tooltipState.openTooltips = newState;
    tooltipState.listeners.forEach((listener) => listener(newState));
  },
  subscribe: (callback: (state: Record<string, boolean>) => void) => {
    tooltipState.listeners.add(callback);
    return () => {
      tooltipState.listeners.delete(callback);
    };
  },
};

// Shared click outside handler - only one listener for all instances
let clickOutsideHandlerAttached = false;
const attachClickOutsideHandler = () => {
  if (clickOutsideHandlerAttached) return;
  clickOutsideHandlerAttached = true;

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Element;

    // Check if click is on a tooltip button
    const clickedButton = target.closest("button");
    const isTooltipButton =
      clickedButton &&
      (clickedButton.querySelector('svg[data-testid="DescriptionIcon"]') !== null ||
        clickedButton.querySelector(".MuiSvgIcon-root") !== null ||
        clickedButton.closest('[class*="MuiIconButton"]') !== null);

    // Check if click is outside any tooltip button and tooltip content
    const isOutsideTooltip =
      !target.closest('[role="tooltip"]') &&
      !target.closest(".MuiTooltip-popper") &&
      !target.closest('[class*="MuiTooltip"]') &&
      !isTooltipButton;

    if (isOutsideTooltip) {
      const hasOpenTooltips = Object.values(tooltipState.openTooltips).some(Boolean);
      if (hasOpenTooltips) {
        tooltipState.setOpenTooltips(() => ({}));
      }
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
};

// Tooltip styles - extracted as constant to avoid recreation
const tooltipStyles = {
  bgcolor: "#333",
  color: "#fff",
  fontSize: "0.875rem",
  maxWidth: 350,
  padding: "12px 16px",
  borderRadius: "4px",
  boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  "& .MuiTooltip-arrow": {
    color: "#333",
    "&::before": {
      borderColor: "#333",
    },
  },
  "&[data-popper-placement^='right'] .MuiTooltip-arrow": {
    left: "8px !important",
    top: "8px !important",
    right: "auto !important",
  },
} as const;

// IconButton styles - extracted as constant
const iconButtonStyles = {
  padding: "2px",
  color: "#666",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
} as const;

interface RowData {
  _id?: string;
  id?: string;
  latestRetryStatus?: {
    action?: string;
    resolutionNote?: string;
    userName?: string;
    timeStamp?: string;
  };
}

interface NotesCellProps {
  row: RowData;
}

const NotesCell: React.FC<NotesCellProps> = React.memo(({ row }) => {
  const rowId = row?._id || row?.id || "";
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  // Subscribe to shared tooltip state
  useEffect(() => {
    const unsubscribe = tooltipState.subscribe((state) => {
      setIsTooltipOpen(state[rowId] || false);
    });
    // Initialize with current state
    setIsTooltipOpen(tooltipState.openTooltips[rowId] || false);
    return unsubscribe;
  }, [rowId]);

  // Attach shared click outside handler (only once globally)
  useEffect(() => {
    attachClickOutsideHandler();
  }, []);

  // Memoize notes-related data
  const { resolutionNote, resolvedBy, resolvedDate } = useMemo(
    () => ({
      resolutionNote: row?.latestRetryStatus?.resolutionNote || "",
      resolvedBy: row?.latestRetryStatus?.userName || "N/A",
      resolvedDate: row?.latestRetryStatus?.timeStamp || "",
    }),
    [
      row?.latestRetryStatus?.resolutionNote,
      row?.latestRetryStatus?.userName,
      row?.latestRetryStatus?.timeStamp,
    ]
  );

  // Memoize formatted date
  const formattedResolvedDate = useMemo(
    () => formatResolvedDate(resolvedDate),
    [resolvedDate]
  );

  // Memoize tooltip content
  const tooltipContent = useMemo(
    () => (
      <Box sx={{ textAlign: "left", maxWidth: 300 }}>
        <Box sx={{ marginBottom: 1 }}>
          {resolutionNote}
        </Box>
        <Box sx={{ marginTop: 2, fontSize: "0.75rem", opacity: 0.9, display: "flex", gap: "16px" }}>
          <Box>Resolved by: {resolvedBy}</Box>
          <Box>Date & Time: {formattedResolvedDate}</Box>
        </Box>
      </Box>
    ),
    [resolutionNote, resolvedBy, formattedResolvedDate]
  );

  const handleTooltipToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      tooltipState.setOpenTooltips((prev) => {
        const isCurrentlyOpen = prev[rowId] || false;
        // If clicking on the same tooltip that's already open, close it
        if (isCurrentlyOpen) {
          return {
            ...prev,
            [rowId]: false,
          };
        } else {
          // Close all tooltips first, then open this one
          return {
            [rowId]: true,
          };
        }
      });
    },
    [rowId]
  );

  const handleTooltipClose = useCallback(() => {
    tooltipState.setOpenTooltips((prev) => ({
      ...prev,
      [rowId]: false,
    }));
  }, [rowId]);

  return (
    <Grid
      container
      alignItems="center"
      spacing={0.5}
      sx={{ display: "inline-flex", width: "auto", justifyContent: "center" }}
    >
      <Grid item>
        <Tooltip
          open={isTooltipOpen}
          onClose={handleTooltipClose}
          title={tooltipContent}
          placement="right"
          arrow
          disableHoverListener
          disableFocusListener
          disableTouchListener
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 8],
                },
              },
            ],
          }}
          componentsProps={{
            tooltip: {
              sx: tooltipStyles,
            },
          }}
        >
          <IconButton
            size="small"
            onClick={handleTooltipToggle}
            sx={iconButtonStyles}
          >
            <DescriptionIcon sx={{ fontSize: "20px", color: "#2469F3" }} />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memoization - check all fields that affect rendering
  const prevId = prevProps.row?._id || prevProps.row?.id;
  const nextId = nextProps.row?._id || nextProps.row?.id;

  if (prevId !== nextId) return false;

  const prevAction = prevProps.row?.latestRetryStatus?.action;
  const nextAction = nextProps.row?.latestRetryStatus?.action;
  if (prevAction !== nextAction) return false;

  // Check notes-related fields that affect tooltip content
  const prevResolutionNote = prevProps.row?.latestRetryStatus?.resolutionNote;
  const nextResolutionNote = nextProps.row?.latestRetryStatus?.resolutionNote;
  if (prevResolutionNote !== nextResolutionNote) return false;

  const prevResolvedBy = prevProps.row?.latestRetryStatus?.userName;
  const nextResolvedBy = nextProps.row?.latestRetryStatus?.userName;
  if (prevResolvedBy !== nextResolvedBy) return false;

  const prevResolvedDate = prevProps.row?.latestRetryStatus?.timeStamp;
  const nextResolvedDate = nextProps.row?.latestRetryStatus?.timeStamp;
  if (prevResolvedDate !== nextResolvedDate) return false;

  return true;
});

NotesCell.displayName = "NotesCell";

export default NotesCell;
